import { parseDepartureTimeConfig } from "./departureTimeConfig";

export const LAYER_FILE = {
  links: "berkeley_links.csv",
  nodes: "berkeley_nodes.csv",
  parcels: "berkeley_parcels.csv",
  polygon: "polygon.geojson",
};

const DEFAULT_LAYER_TYPES = ["links", "nodes", "parcels"];
export const LAYER_TYPES = [...DEFAULT_LAYER_TYPES, "other", "polygon"];

export const LAYER_ID_KEYS = {
  links: ["link_id", "linkid", "id"],
  nodes: ["node_id", "nodeid", "id"],
  parcels: ["parcel_id", "parcelid", "id"],
  other: [],
  polygon: ["polygon_id", "feature_id", "id"],
};

export const CREATE_LAYER_TYPE_OPTIONS = Object.freeze([
  { value: "links", label: "links (CSV)" },
  { value: "nodes", label: "nodes (CSV)" },
  { value: "parcels", label: "parcels (CSV)" },
  { value: "other", label: "other (CSV)" },
  { value: "polygon", label: "polygon (GeoJSON)" },
]);

const CREATE_LAYER_TYPES = CREATE_LAYER_TYPE_OPTIONS.map((row) => row.value);
const CSV_FILE_ACCEPT = ".csv,text/csv";
const GEOJSON_FILE_ACCEPT = ".geojson,.json,application/geo+json,application/json";

function normalizeHeader(header) {
  return String(header || "").trim().toLowerCase();
}

function hasHeader(headersSet, candidates = []) {
  return candidates.some((candidate) => headersSet.has(normalizeHeader(candidate)));
}

export function normalizeCreateLayerType(rawType) {
  return String(rawType || "").trim().toLowerCase();
}

export function isCreateLayerTypeSupported(rawType) {
  return CREATE_LAYER_TYPES.includes(normalizeCreateLayerType(rawType));
}

export function createLayerAcceptForType(rawType) {
  const type = normalizeCreateLayerType(rawType);
  return type === "polygon" ? GEOJSON_FILE_ACCEPT : CSV_FILE_ACCEPT;
}

export function createLayerFileExtensionForType(rawType) {
  const type = normalizeCreateLayerType(rawType);
  return type === "polygon" ? ".geojson" : ".csv";
}

export function validateCreateLayerFileName(rawType, rawFileName) {
  const type = normalizeCreateLayerType(rawType);
  const fileName = String(rawFileName || "").toLowerCase();

  if (type === "polygon") {
    if (fileName.endsWith(".geojson") || fileName.endsWith(".json")) return "";
    return "Polygon layers only support .geojson or .json uploads.";
  }

  if (fileName.endsWith(".csv")) return "";

  if (type === "other") return "Custom row layers only support CSV uploads.";
  return `${type} layers only support CSV uploads.`;
}

export function validateCreateLayerHeaders(rawType, headers = []) {
  const type = normalizeCreateLayerType(rawType);
  if (type === "other" || type === "polygon") return "";

  const normalizedHeaders = new Set(
    (Array.isArray(headers) ? headers : []).map((h) => normalizeHeader(h)).filter(Boolean)
  );

  if (type === "links") {
    const hasId = hasHeader(normalizedHeaders, LAYER_ID_KEYS.links);
    const hasGeometry = hasHeader(normalizedHeaders, ["geometry", "geom", "wkt"]);
    if (hasId && hasGeometry) return "";
    const missing = [];
    if (!hasId) missing.push("link id (link_id/linkid/id)");
    if (!hasGeometry) missing.push("geometry (geometry/geom/wkt)");
    return `Missing required columns for links: ${missing.join(", ")}.`;
  }

  if (type === "nodes") {
    const hasId = hasHeader(normalizedHeaders, LAYER_ID_KEYS.nodes);
    const hasLat = hasHeader(normalizedHeaders, ["lat", "latitude"]);
    const hasLng = hasHeader(normalizedHeaders, ["lon", "lng", "longitude"]);
    if (hasId && hasLat && hasLng) return "";
    const missing = [];
    if (!hasId) missing.push("node id (node_id/nodeid/id)");
    if (!hasLat) missing.push("latitude (lat/latitude)");
    if (!hasLng) missing.push("longitude (lon/lng/longitude)");
    return `Missing required columns for nodes: ${missing.join(", ")}.`;
  }

  if (type === "parcels") {
    const hasId = hasHeader(normalizedHeaders, LAYER_ID_KEYS.parcels);
    const hasLat = hasHeader(normalizedHeaders, ["lat", "latitude"]);
    const hasLng = hasHeader(normalizedHeaders, ["lon", "lng", "longitude"]);
    if (hasId && hasLat && hasLng) return "";
    const missing = [];
    if (!hasId) missing.push("parcel id (parcel_id/parcelid/id)");
    if (!hasLat) missing.push("latitude (lat/latitude)");
    if (!hasLng) missing.push("longitude (lon/lng/longitude)");
    return `Missing required columns for parcels: ${missing.join(", ")}.`;
  }

  return "";
}

export function noValidRowsMessageForLayerType(rawType) {
  const type = normalizeCreateLayerType(rawType);
  if (type === "links") {
    return "No valid link rows found. Ensure id and LINESTRING geometry are present.";
  }
  if (type === "nodes") {
    return "No valid node rows found. Ensure id and lat/lon columns are present.";
  }
  if (type === "parcels") {
    return "No valid parcel rows found. Ensure id and lat/lon columns are present.";
  }
  if (type === "polygon") {
    return "No Polygon or MultiPolygon features were found in this GeoJSON file.";
  }
  return "No rows were found in the uploaded file.";
}

export const REQUIRED_LINK_FIELDS = [
  { candidates: ["id", "link_id", "linkid"], fallback: "id" },
  { candidates: ["start_node_id", "from_node_id"], fallback: "start_node_id" },
  { candidates: ["end_node_id", "to_node_id"], fallback: "end_node_id" },
  { candidates: ["type"], fallback: "type" },
  { candidates: ["name", "link_name"], fallback: "name" },
  { candidates: ["length"], fallback: "length" },
  { candidates: ["maxmph"], fallback: "maxmph" },
  { candidates: ["lanes"], fallback: "lanes" },
  { candidates: ["capacity"], fallback: "capacity" },
  { candidates: ["fft"], fallback: "fft" },
  { candidates: ["start_osmid"], fallback: "start_osmid" },
  { candidates: ["end_osmid"], fallback: "end_osmid" },
  { candidates: ["geometry", "geom", "wkt"], fallback: "geometry" },
];

export function normalizeLatLng(a, b) {
  // WKT is usually "x y" => "lng lat"
  let lng = Number(a);
  let lat = Number(b);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  // Fix swapped order if it looks swapped
  if (Math.abs(lat) > 90 && Math.abs(lng) <= 90) {
    [lat, lng] = [lng, lat];
  }

  // Reject impossible values (Google drops these silently)
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;

  return { lat, lng };
}

export function parseLineStringToPath(wkt) {
  if (!wkt || typeof wkt !== "string") return null;

  const m = wkt.trim().match(/^LINESTRING(?:\s+Z)?\s*\((.+)\)\s*$/i);
  if (!m) return null;

  const pts = m[1]
    .split(",")
    .map((s) => s.trim().split(/\s+/))
    .map(([a, b]) => normalizeLatLng(a, b))
    .filter(Boolean);

  return pts.length >= 2 ? pts : null;
}

export function pointAndHeadingAlongPath(path, fraction) {
  if (!path?.length) return null;
  if (path.length === 1) return { position: path[0], rotation: 0 };
  const lengths = path.slice(1).map((point, index) => {
    const previous = path[index];
    const latitudeScale = Math.cos(((point.lat + previous.lat) / 2) * Math.PI / 180);
    return Math.hypot(point.lat - previous.lat, (point.lng - previous.lng) * latitudeScale);
  });
  const total = lengths.reduce((sum, length) => sum + length, 0);
  if (!total) return { position: path[Math.floor(path.length / 2)], rotation: 0 };
  let remaining = total * Math.max(0, Math.min(1, fraction));
  for (let index = 0; index < lengths.length; index += 1) {
    if (lengths[index] > 0 && (remaining <= lengths[index] || index === lengths.length - 1)) {
      const fraction = remaining / lengths[index];
      const start = path[index];
      const end = path[index + 1];
      const latitudeScale = Math.cos(((start.lat + end.lat) / 2) * Math.PI / 180);
      return {
        position: {
          lat: start.lat + (end.lat - start.lat) * fraction,
          lng: start.lng + (end.lng - start.lng) * fraction,
        },
        rotation: Math.atan2(start.lat - end.lat, (end.lng - start.lng) * latitudeScale) * 180 / Math.PI,
      };
    }
    remaining -= lengths[index];
  }
  return { position: path[path.length - 1], rotation: 0 };
}

export function pathMidpoint(path) {
  return pointAndHeadingAlongPath(path, 0.5)?.position || null;
}

export function blockedRoadSegments(links) {
  const used = new Set();
  const endpoint = (link, side) => {
    const node = side === "start" ? link.startNodeId : link.endNodeId;
    const point = side === "start" ? link.path[0] : link.path[link.path.length - 1];
    return node || (point ? `${point.lat.toFixed(5)},${point.lng.toFixed(5)}` : "");
  };
  return links.flatMap((link, index) => {
    if (used.has(index)) return [];
    used.add(index);
    const start = endpoint(link, "start");
    const end = endpoint(link, "end");
    const reverseIndex = start && end && start !== end ? links.findIndex((candidate, candidateIndex) =>
      candidateIndex !== index && !used.has(candidateIndex) &&
      (!link.name || !candidate.name || link.name === candidate.name) &&
      endpoint(candidate, "start") === end && endpoint(candidate, "end") === start
    ) : -1;
    if (reverseIndex >= 0) used.add(reverseIndex);
    return [{ id: link.id, path: link.path, center: pathMidpoint(link.path), arrow: reverseIndex < 0 ? pointAndHeadingAlongPath(link.path, 0.72) : null, twoSided: reverseIndex >= 0 }];
  });
}

export function pointInViewportBounds(point, bounds) {
  if (!bounds) return true;
  const lat = Number(point?.lat);
  const lng = Number(point?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < bounds.south || lat > bounds.north) return false;

  // Handle dateline crossing: west > east means two longitude ranges.
  if (bounds.west <= bounds.east) {
    return lng >= bounds.west && lng <= bounds.east;
  }
  return lng >= bounds.west || lng <= bounds.east;
}

function getViewportLngRanges(bounds) {
  if (!bounds) return [];
  if (bounds.west <= bounds.east) return [[bounds.west, bounds.east]];
  return [[bounds.west, 180], [-180, bounds.east]];
}

function orientation(a, b, c) {
  const value = (b.lat - a.lat) * (c.lng - b.lng) - (b.lng - a.lng) * (c.lat - b.lat);
  if (Math.abs(value) < 1e-12) return 0;
  return value > 0 ? 1 : 2;
}

function pointOnSegment(a, b, c) {
  return (
    Math.min(a.lng, c.lng) <= b.lng + 1e-12 &&
    b.lng <= Math.max(a.lng, c.lng) + 1e-12 &&
    Math.min(a.lat, c.lat) <= b.lat + 1e-12 &&
    b.lat <= Math.max(a.lat, c.lat) + 1e-12
  );
}

function segmentsIntersect(a, b, c, d) {
  const o1 = orientation(a, b, c);
  const o2 = orientation(a, b, d);
  const o3 = orientation(c, d, a);
  const o4 = orientation(c, d, b);

  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && pointOnSegment(a, c, b)) return true;
  if (o2 === 0 && pointOnSegment(a, d, b)) return true;
  if (o3 === 0 && pointOnSegment(c, a, d)) return true;
  if (o4 === 0 && pointOnSegment(c, b, d)) return true;
  return false;
}

function segmentIntersectsViewportBounds(a, b, bounds) {
  if (!bounds) return true;
  if (pointInViewportBounds(a, bounds) || pointInViewportBounds(b, bounds)) return true;

  const latMin = bounds.south;
  const latMax = bounds.north;
  if (Math.max(a.lat, b.lat) < latMin || Math.min(a.lat, b.lat) > latMax) return false;

  return getViewportLngRanges(bounds).some(([lngMin, lngMax]) => {
    if (Math.max(a.lng, b.lng) < lngMin || Math.min(a.lng, b.lng) > lngMax) return false;

    const topLeft = { lat: latMax, lng: lngMin };
    const topRight = { lat: latMax, lng: lngMax };
    const bottomRight = { lat: latMin, lng: lngMax };
    const bottomLeft = { lat: latMin, lng: lngMin };

    return (
      segmentsIntersect(a, b, topLeft, topRight) ||
      segmentsIntersect(a, b, topRight, bottomRight) ||
      segmentsIntersect(a, b, bottomRight, bottomLeft) ||
      segmentsIntersect(a, b, bottomLeft, topLeft)
    );
  });
}

export function pathIntersectsViewportBounds(path, bounds) {
  if (!bounds) return true;
  if (!Array.isArray(path) || !path.length) return false;

  let prev = null;

  for (const point of path) {
    const lat = Number(point?.lat);
    const lng = Number(point?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const current = { lat, lng };
    if (pointInViewportBounds(current, bounds)) return true;
    if (prev && segmentIntersectsViewportBounds(prev, current, bounds)) return true;
    prev = current;
  }

  return false;
}

function parsePolygonRing(coords = []) {
  const ring = (Array.isArray(coords) ? coords : [])
    .map((pair) => {
      if (!Array.isArray(pair) || pair.length < 2) return null;
      return normalizeLatLng(pair[0], pair[1]);
    })
    .filter(Boolean);
  if (ring.length < 3) return null;
  return ring;
}

function parsePolygonRings(coords = []) {
  const rings = (Array.isArray(coords) ? coords : [])
    .map((ring) => parsePolygonRing(ring))
    .filter(Boolean);
  return rings.length ? rings : null;
}

function geometryToPolygonPaths(geometry) {
  const type = String(geometry?.type || "").toLowerCase();
  if (type === "polygon") {
    const rings = parsePolygonRings(geometry?.coordinates || []);
    return rings ? [rings] : [];
  }
  if (type === "multipolygon") {
    return (Array.isArray(geometry?.coordinates) ? geometry.coordinates : [])
      .map((poly) => parsePolygonRings(poly))
      .filter(Boolean);
  }
  return [];
}

function sameLngLat(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  return Number(a[0]) === Number(b[0]) && Number(a[1]) === Number(b[1]);
}

function normalizeRingPosition(rawPos) {
  if (!Array.isArray(rawPos) || rawPos.length < 2) return null;
  const lng = Number(rawPos[0]);
  const lat = Number(rawPos[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return [lng, lat];
}

function sanitizeLinearRing(rawRing = [], stats) {
  if (!Array.isArray(rawRing)) {
    stats.removedInvalidRings += 1;
    return null;
  }

  const normalized = [];
  for (const pos of rawRing) {
    const parsed = normalizeRingPosition(pos);
    if (!parsed) continue;
    const prev = normalized[normalized.length - 1];
    if (prev && sameLngLat(prev, parsed)) {
      stats.removedDuplicateVertices += 1;
      continue;
    }
    normalized.push(parsed);
  }

  if (normalized.length < 3) {
    stats.removedInvalidRings += 1;
    return null;
  }

  const first = normalized[0];
  const last = normalized[normalized.length - 1];
  if (!sameLngLat(first, last)) {
    normalized.push([...first]);
    stats.closedRings += 1;
  }

  if (normalized.length < 4) {
    stats.removedInvalidRings += 1;
    return null;
  }

  const uniqueVertices = new Set(
    normalized.slice(0, -1).map((p) => `${Number(p[0])},${Number(p[1])}`)
  );
  if (uniqueVertices.size < 3) {
    stats.removedInvalidRings += 1;
    return null;
  }

  return normalized;
}

function sanitizePolygonCoordinates(rawPolygonCoords = [], stats) {
  if (!Array.isArray(rawPolygonCoords) || !rawPolygonCoords.length) return null;
  const outer = sanitizeLinearRing(rawPolygonCoords[0], stats);
  if (!outer) return null;

  const holes = rawPolygonCoords
    .slice(1)
    .map((ring) => sanitizeLinearRing(ring, stats))
    .filter(Boolean);

  return [outer, ...holes];
}

function sanitizePolygonGeometry(geometry, stats) {
  const type = String(geometry?.type || "").toLowerCase();
  if (type === "polygon") {
    const coords = sanitizePolygonCoordinates(geometry?.coordinates || [], stats);
    return coords ? { ...geometry, coordinates: coords } : null;
  }

  if (type === "multipolygon") {
    const rawPolygons = Array.isArray(geometry?.coordinates) ? geometry.coordinates : [];
    const polygons = rawPolygons
      .map((polygonCoords) => sanitizePolygonCoordinates(polygonCoords, stats))
      .filter(Boolean);
    if (!polygons.length) return null;
    return { ...geometry, coordinates: polygons };
  }

  return geometry;
}

export function sanitizePolygonGeoJsonUpload(rawText) {
  const text = String(rawText || "").trim();
  if (!text) {
    return {
      ok: false,
      error: "Uploaded GeoJSON file is empty.",
    };
  }

  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return {
      ok: false,
      error: "Uploaded file is not valid JSON.",
    };
  }

  const stats = {
    removedDuplicateVertices: 0,
    removedInvalidRings: 0,
    removedInvalidGeometries: 0,
    closedRings: 0,
  };

  const parsedType = String(parsed?.type || "").toLowerCase();
  let next = parsed;

  if (parsedType === "featurecollection") {
    const nextFeatures = (Array.isArray(parsed?.features) ? parsed.features : []).map((feature) => {
      const geometry = feature?.geometry;
      if (!geometry) return feature;
      const nextGeometry = sanitizePolygonGeometry(geometry, stats);
      if (!nextGeometry) {
        stats.removedInvalidGeometries += 1;
        return { ...feature, geometry: null };
      }
      return { ...feature, geometry: nextGeometry };
    });
    next = { ...parsed, features: nextFeatures };
  } else if (parsedType === "feature") {
    const geometry = parsed?.geometry;
    if (geometry) {
      const nextGeometry = sanitizePolygonGeometry(geometry, stats);
      if (!nextGeometry) {
        stats.removedInvalidGeometries += 1;
        next = { ...parsed, geometry: null };
      } else {
        next = { ...parsed, geometry: nextGeometry };
      }
    }
  } else if (parsed?.type && parsed?.coordinates) {
    const nextGeometry = sanitizePolygonGeometry(parsed, stats);
    if (!nextGeometry) {
      return {
        ok: false,
        error: "GeoJSON geometry is invalid after cleanup.",
      };
    }
    next = nextGeometry;
  }

  const featuresAfterSanitize = parsePolygonGeoJsonFeatures(JSON.stringify(next));
  if (!featuresAfterSanitize.length) {
    return {
      ok: false,
      error: "No valid Polygon or MultiPolygon features remain after cleanup.",
    };
  }

  return {
    ok: true,
    text: JSON.stringify(next, null, 2),
    stats,
  };
}

export function parsePolygonGeoJsonFeatures(rawText) {
  const text = String(rawText || "").trim();
  if (!text) return [];

  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return [];
  }

  const parsedType = String(parsed?.type || "").toLowerCase();
  const features = [];
  if (parsedType === "featurecollection") {
    (Array.isArray(parsed?.features) ? parsed.features : []).forEach((feature) => features.push(feature));
  } else if (parsedType === "feature") {
    features.push(parsed);
  } else if (parsed?.type && parsed?.coordinates) {
    features.push({ type: "Feature", geometry: parsed, properties: {} });
  }

  const out = [];
  features.forEach((feature, featureIdx) => {
    const geometry = feature?.geometry || null;
    const polyPaths = geometryToPolygonPaths(geometry);
    if (!polyPaths.length) return;

    const props = feature?.properties || {};
    const rawId =
      props?.id ??
      props?.zoneId ??
      props?.ZoneID ??
      props?.name ??
      feature?.id ??
      `poly_${featureIdx + 1}`;
    const rawName =
      props?.name ??
      props?.ZoneID ??
      props?.zoneId ??
      props?.id ??
      feature?.id ??
      rawId;
    const baseId = String(rawId || `poly_${featureIdx + 1}`).trim() || `poly_${featureIdx + 1}`;
    const baseName = String(rawName || baseId).trim() || baseId;
    const geometryType = String(geometry?.type || "").trim();

    polyPaths.forEach((paths, polyIdx) => {
      const needsSuffix = polyPaths.length > 1;
      const suffix = needsSuffix ? `_${polyIdx + 1}` : "";
      out.push({
        polygon_id: `${baseId}${suffix}`,
        feature_name: needsSuffix ? `${baseName} ${polyIdx + 1}` : baseName,
        geometry_type: geometryType,
        paths,
      });
    });
  });

  return out;
}

export function parseHazardGeoJsonSnapshot(rawText) {
  const sanitized = sanitizePolygonGeoJsonUpload(rawText);
  if (!sanitized?.ok) return sanitized;

  let geojson;
  try {
    geojson = JSON.parse(sanitized.text);
  } catch {
    return { ok: false, error: "Uploaded file is not valid JSON." };
  }
  if (String(geojson?.type || "").toLowerCase() !== "featurecollection") {
    return { ok: false, error: "Hazard uploads must be GeoJSON FeatureCollections." };
  }

  const features = Array.isArray(geojson.features) ? geojson.features : [];
  if (!features.length) return { ok: false, error: "Hazard GeoJSON must contain at least one feature." };

  const timeValues = new Set();
  const rows = [];
  for (let featureIdx = 0; featureIdx < features.length; featureIdx += 1) {
    const feature = features[featureIdx];
    const geometryType = String(feature?.geometry?.type || "").toLowerCase();
    if (geometryType !== "polygon" && geometryType !== "multipolygon") {
      return { ok: false, error: `Feature ${featureIdx + 1} must have Polygon or MultiPolygon geometry.` };
    }
    const rawTime = feature?.properties?.time_min;
    const timeMin = Number(rawTime);
    if (rawTime == null || rawTime === "" || !Number.isFinite(timeMin)) {
      return { ok: false, error: `Feature ${featureIdx + 1} requires a numeric properties.time_min.` };
    }
    timeValues.add(timeMin);
    const parsedRows = parsePolygonGeoJsonFeatures(JSON.stringify(feature));
    parsedRows.forEach((row, polygonIdx) => {
      rows.push({
        ...row,
        polygon_id: `hazard_${featureIdx + 1}_${polygonIdx + 1}`,
        time_min: timeMin,
        feature_index: featureIdx + 1,
        properties: { ...(feature?.properties || {}) },
      });
    });
  }

  if (timeValues.size !== 1) {
    return { ok: false, error: "All features in one hazard GeoJSON must have the same time_min." };
  }
  if (!rows.length) return { ok: false, error: "No valid hazard polygons remain after cleanup." };

  const [timeMin] = [...timeValues];
  return {
    ok: true,
    text: sanitized.text,
    geojson,
    rows,
    timeMin,
    featureCount: features.length,
    geometryTypes: [...new Set(features.map((feature) => String(feature?.geometry?.type || "")))],
    stats: sanitized.stats,
  };
}

export function pathToLineString(path = []) {
  if (!Array.isArray(path) || path.length < 2) return "";
  const coords = path.map((p) => `${Number(p.lng)} ${Number(p.lat)}`).join(", ");
  return `LINESTRING(${coords})`;
}

export function findHeader(fields = [], candidates = [], fallback) {
  const lowerToField = {};
  fields.forEach((f) => {
    lowerToField[String(f).toLowerCase()] = f;
  });
  for (const c of candidates) {
    const hit = lowerToField[String(c).toLowerCase()];
    if (hit) return hit;
  }
  return fallback;
}

export function upsertRowValue(fields, row, candidates, fallback, value) {
  const header = findHeader(fields, candidates, fallback);
  if (!fields.includes(header)) fields.push(header);
  row[header] = value;
  return header;
}

export function sqDist(a, b) {
  const dx = Number(a?.lat) - Number(b?.lat);
  const dy = Number(a?.lng) - Number(b?.lng);
  return dx * dx + dy * dy;
}

function haversineMeters(a, b) {
  const lat1 = Number(a?.lat) * (Math.PI / 180);
  const lng1 = Number(a?.lng) * (Math.PI / 180);
  const lat2 = Number(b?.lat) * (Math.PI / 180);
  const lng2 = Number(b?.lng) * (Math.PI / 180);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return 0;
  const dLat = lat2 - lat1;
  const dLng = lng2 - lng1;
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const aa = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
  const c = 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
  return 6371000 * c;
}

export function pathLengthMeters(path = []) {
  if (!Array.isArray(path) || path.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < path.length; i += 1) {
    total += haversineMeters(path[i - 1], path[i]);
  }
  return total;
}

export function getLinkStartNodeId(row) {
  return String(row?.start_node_id ?? row?.from_node_id ?? "");
}

export function getLinkEndNodeId(row) {
  return String(row?.end_node_id ?? row?.to_node_id ?? "");
}

export function withLinkNodeIds(row, startId, endId) {
  const next = { ...row };
  if ("start_node_id" in row || !("from_node_id" in row)) next.start_node_id = String(startId);
  if ("end_node_id" in row || !("to_node_id" in row)) next.end_node_id = String(endId);
  if ("from_node_id" in row) next.from_node_id = String(startId);
  if ("to_node_id" in row) next.to_node_id = String(endId);
  return next;
}

function closestPointOnSegment(a, b, p) {
  const ax = Number(a?.lng);
  const ay = Number(a?.lat);
  const bx = Number(b?.lng);
  const by = Number(b?.lat);
  const px = Number(p?.lng);
  const py = Number(p?.lat);
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const ab2 = abx * abx + aby * aby;
  const t = ab2 > 0 ? Math.max(0, Math.min(1, (apx * abx + apy * aby) / ab2)) : 0;
  const qx = ax + abx * t;
  const qy = ay + aby * t;
  const dx = px - qx;
  const dy = py - qy;
  return {
    point: { lat: qy, lng: qx },
    t,
    dist2: dx * dx + dy * dy,
  };
}

export function closestPointOnPath(path = [], p) {
  if (!Array.isArray(path) || path.length < 2) return null;
  let best = null;
  for (let i = 0; i < path.length - 1; i += 1) {
    const s = closestPointOnSegment(path[i], path[i + 1], p);
    if (!best || s.dist2 < best.dist2) {
      best = { ...s, segIdx: i };
    }
  }
  return best;
}

export function splitPathAtProjection(path = [], proj) {
  if (!proj || !Array.isArray(path) || path.length < 2) return null;
  const foot = proj.point;
  const i = proj.segIdx;
  const left = [...path.slice(0, i + 1), foot];
  const right = [foot, ...path.slice(i + 1)];
  return { left, right, foot };
}

function toScalarString(v) {
  if (v == null) return "";
  return String(v);
}

function buildLowerKeyMap(row = {}) {
  const m = {};
  Object.keys(row || {}).forEach((k) => {
    m[String(k).toLowerCase()] = k;
  });
  return m;
}

function ensureSingleLinkNodeRef(linkFields, linkOut, value, direction = "from") {
  if (direction === "from") {
    if (findHeader(linkFields, ["start_node_id"], "")) {
      upsertRowValue(linkFields, linkOut, ["start_node_id"], "start_node_id", value);
      return;
    }
    if (findHeader(linkFields, ["from_node_id"], "")) {
      upsertRowValue(linkFields, linkOut, ["from_node_id"], "from_node_id", value);
      return;
    }
    upsertRowValue(linkFields, linkOut, ["start_node_id"], "start_node_id", value);
    return;
  }

  if (findHeader(linkFields, ["end_node_id"], "")) {
    upsertRowValue(linkFields, linkOut, ["end_node_id"], "end_node_id", value);
    return;
  }
  if (findHeader(linkFields, ["to_node_id"], "")) {
    upsertRowValue(linkFields, linkOut, ["to_node_id"], "to_node_id", value);
    return;
  }
  upsertRowValue(linkFields, linkOut, ["end_node_id"], "end_node_id", value);
}

export function makeLinkCsvRow(row, linkFields) {
  const out = {};
  linkFields.forEach((f) => {
    out[f] = "";
  });
  const lower = buildLowerKeyMap(row || {});
  linkFields.forEach((f) => {
    const key = lower[String(f).toLowerCase()] || f;
    const val = row?.[key];
    if (typeof val !== "object") out[f] = toScalarString(val);
  });

  const idVal = String(row?.link_id ?? row?.id ?? "");
  upsertRowValue(linkFields, out, LAYER_ID_KEYS.links, "id", idVal);
  ensureSingleLinkNodeRef(linkFields, out, String(getLinkStartNodeId(row) || ""), "from");
  ensureSingleLinkNodeRef(linkFields, out, String(getLinkEndNodeId(row) || ""), "to");

  const path = Array.isArray(row?.path)
    ? row.path
    : parseLineStringToPath(row?.geometry || row?.geom || row?.wkt);
  if (Array.isArray(path) && path.length >= 2) {
    upsertRowValue(linkFields, out, ["geometry", "geom", "wkt"], "geometry", pathToLineString(path));
    upsertRowValue(linkFields, out, ["length"], "length", String(pathLengthMeters(path)));
  }
  return out;
}

export function makeNodeCsvRow(row, nodeFields) {
  const out = {};
  nodeFields.forEach((f) => {
    out[f] = "";
  });
  const lower = buildLowerKeyMap(row || {});
  nodeFields.forEach((f) => {
    const key = lower[String(f).toLowerCase()] || f;
    const val = row?.[key];
    if (typeof val !== "object") out[f] = toScalarString(val);
  });
  upsertRowValue(nodeFields, out, LAYER_ID_KEYS.nodes, "node_id", String(row?.node_id ?? row?.id ?? ""));
  upsertRowValue(nodeFields, out, ["lat", "latitude"], "lat", toScalarString(row?.lat));
  upsertRowValue(nodeFields, out, ["lon", "lng", "longitude"], "lon", toScalarString(row?.lng ?? row?.lon));
  return out;
}

function nonEmptyScalar(value) {
  if (value == null) return "";
  const text = String(value).trim();
  return text;
}

function parcelPopulationKey(row = {}, draft = {}) {
  const hasPopulation =
    Object.prototype.hasOwnProperty.call(draft || {}, "population") ||
    Object.prototype.hasOwnProperty.call(row || {}, "population");
  if (hasPopulation) return "population";

  const hasPop =
    Object.prototype.hasOwnProperty.call(draft || {}, "pop") ||
    Object.prototype.hasOwnProperty.call(row || {}, "pop") ||
    nonEmptyScalar((draft || {}).pop ?? (row || {}).pop ?? "") !== "";
  return hasPop ? "pop" : "";
}

function readParcelCriticalType(row = {}, draft = {}) {
  if (Object.prototype.hasOwnProperty.call(draft || {}, "type_internal")) {
    return nonEmptyScalar(draft?.type_internal);
  }
  return nonEmptyScalar(row?.type_internal);
}

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function isParcelCriticalFacility(row = {}, draft = {}) {
  return readParcelCriticalType(row, draft).toLowerCase() === "critical facility";
}

export function applyCriticalFacilityToParcelDraft(row = {}, draft = {}) {
  const out = { ...(draft || {}) };
  const popKey = parcelPopulationKey(row, draft);
  const currentPopValue = popKey
    ? nonEmptyScalar((draft || {})[popKey] ?? (row || {})[popKey] ?? "")
    : "";

  const existingMin = nonEmptyScalar((draft || {}).critical_facility_min ?? (row || {}).critical_facility_min ?? "");
  const existingMax = nonEmptyScalar((draft || {}).critical_facility_max ?? (row || {}).critical_facility_max ?? "");
  const seedValue = nonEmptyScalar(existingMin || existingMax || currentPopValue);

  if (popKey) out[popKey] = "0";
  out.critical_facility_min = seedValue;
  out.critical_facility_max = seedValue;
  out.type_internal = "critical facility";

  return out;
}

export function removeCriticalFacilityFromParcelDraft(row = {}, draft = {}) {
  const out = { ...(draft || {}) };
  if (!isParcelCriticalFacility(row, draft)) return out;

  const popKey = parcelPopulationKey(row, draft);
  const minRaw = nonEmptyScalar((draft || {}).critical_facility_min ?? (row || {}).critical_facility_min ?? "");
  const maxRaw = nonEmptyScalar((draft || {}).critical_facility_max ?? (row || {}).critical_facility_max ?? "");
  const minNum = toFiniteNumber(minRaw);
  const maxNum = toFiniteNumber(maxRaw);
  const canAverage = minNum != null && maxNum != null;
  const avgInt = canAverage ? Math.round((minNum + maxNum) / 2) : null;

  if (avgInt != null) {
    if (popKey) out[popKey] = String(avgInt);
    else out.pop = String(avgInt);
  }
  out.critical_facility_min = "";
  out.critical_facility_max = "";
  out.type_internal = "";

  return out;
}

export function isDraftLikeRow(row) {
  if (!row) return false;
  if (row.__isDraft) return true;
  const status = String(row.status ?? row.state ?? "").toLowerCase().trim();
  return status === "draft";
}

export function parseLayerDoc(rawId, data = {}) {
  const type = String(data?.type || "").toLowerCase();
  if (!LAYER_TYPES.includes(type)) return null;
  const id = String(data?.id || rawId || "");
  const filename = String(data?.filename || "").trim();
  if (!id || !filename) return null;
  const name = String(data?.name || `${type}_${id}`).trim();
  return { id, name, filename, type };
}

export function defaultLayers() {
  return DEFAULT_LAYER_TYPES.map((type) => ({
    id: `${type}_default`,
    name: `${type[0].toUpperCase()}${type.slice(1)} (default)`,
    filename: LAYER_FILE[type],
    type,
  }));
}

export function duplicatedFileNameFromId(layerId, sourceFilename) {
  const id = String(layerId || "").trim();
  const src = String(sourceFilename || "").trim();
  if (!id) return "";
  const slashIdx = Math.max(src.lastIndexOf("/"), src.lastIndexOf("\\"));
  const base = slashIdx >= 0 ? src.slice(slashIdx + 1) : src;
  const dotIdx = base.lastIndexOf(".");
  const hasExt = dotIdx > 0 && dotIdx < base.length - 1;
  const ext = hasExt ? base.slice(dotIdx) : "";
  return `${id}${ext}`;
}

export function contentTypeForFileName(filename) {
  const name = String(filename || "").toLowerCase();
  if (name.endsWith(".csv")) return "text/csv";
  if (name.endsWith(".txt")) return "text/plain";
  if (name.endsWith(".geojson")) return "application/geo+json";
  if (name.endsWith(".json")) return "application/json";
  return "text/plain";
}

export function sortCustomConfigVars(rows = []) {
  const priorityByName = {
    destinations: 0,
    departure_times: 1,
  };
  return [...rows].sort((a, b) => {
    const aName = String(a?.name || "");
    const bName = String(b?.name || "");
    const aPriority = Object.prototype.hasOwnProperty.call(priorityByName, aName)
      ? priorityByName[aName]
      : Number.POSITIVE_INFINITY;
    const bPriority = Object.prototype.hasOwnProperty.call(priorityByName, bName)
      ? priorityByName[bName]
      : Number.POSITIVE_INFINITY;
    if (aPriority !== bPriority) return aPriority - bPriority;
    return aName.localeCompare(bName);
  });
}

export function normalizeDestinationIds(values) {
  const out = [];
  const seen = new Set();
  (Array.isArray(values) ? values : []).forEach((raw) => {
    if (raw == null) return;
    const id = String(raw).trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

export function toNumericDestinationIds(values) {
  return normalizeDestinationIds(values).map((id) => {
    const n = Number(id);
    if (Number.isFinite(n) && String(Math.trunc(n)) === id) {
      return Math.trunc(n);
    }
    return id;
  });
}

export function normalizeContraflowLinkIds(values) {
  return normalizeContraflowEntries(values).map((entry) => entry.linkId);
}

export function normalizeBlockedRoadEntries(values) {
  const seen = new Set();
  return (Array.isArray(values) ? values : []).reduce((entries, raw) => {
    const linkId = String(raw && typeof raw === "object" ? (raw.linkId ?? raw.link_id ?? "") : (raw ?? "")).trim();
    if (linkId && !seen.has(linkId)) {
      seen.add(linkId);
      entries.push({ linkId });
    }
    return entries;
  }, []);
}

export function serializeBlockedRoadEntries(values) {
  return normalizeBlockedRoadEntries(values).map(({ linkId }) => {
    const numericId = Number(linkId);
    return { link_id: Number.isSafeInteger(numericId) && String(numericId) === linkId ? numericId : linkId };
  });
}

export function parseBlockedRoadConfigEntry(raw) {
  if (Array.isArray(raw)) return { type: "blocked_road", linksLayerId: "", blockedRoadEntries: normalizeBlockedRoadEntries(raw) };
  if (!raw || typeof raw !== "object" || String(raw.type || "").toLowerCase() !== "blocked_road") return null;
  return {
    type: "blocked_road",
    linksLayerId: String(raw.linksLayerId || raw.links_layer_id || "").trim(),
    blockedRoadEntries: normalizeBlockedRoadEntries(raw.value || raw.values || raw.blockedRoadEntries || []),
  };
}

export function normalizeContraflowEntries(values) {
  const out = [];
  const seen = new Set();
  (Array.isArray(values) ? values : []).forEach((raw) => {
    const isObject = raw && typeof raw === "object" && !Array.isArray(raw);
    const linkId = String(isObject ? (raw.linkId ?? raw.link_id ?? "") : (raw ?? "")).trim();
    if (!linkId || seen.has(linkId)) return;
    seen.add(linkId);
    const reverseLinkId = String(isObject ? (raw.reverseLinkId ?? raw.reverse_link_id ?? "") : "").trim();
    const mainLanesRaw = isObject ? (raw.mainLanes ?? raw.main_lanes) : null;
    const reverseLanesRaw = isObject ? (raw.reverseLanes ?? raw.reverse_lanes) : null;
    const mainLanes = mainLanesRaw == null || mainLanesRaw === "" ? null : Number(mainLanesRaw);
    const reverseLanes = reverseLanesRaw == null || reverseLanesRaw === "" ? null : Number(reverseLanesRaw);
    out.push({
      linkId,
      reverseLinkId,
      mainLanes: Number.isInteger(mainLanes) && mainLanes >= 0 ? mainLanes : null,
      reverseLanes: Number.isInteger(reverseLanes) && reverseLanes >= 0 ? reverseLanes : null,
    });
  });
  return out;
}

export function parseContraflowConfigEntry(raw) {
  if (Array.isArray(raw)) {
    return {
      type: "contraflow",
      linksLayerId: "",
      destinationConfigName: "",
      contraflowEntries: normalizeContraflowEntries(raw),
    };
  }
  if (!raw || typeof raw !== "object") return null;
  const type = String(raw.type || "contraflow").toLowerCase();
  if (type !== "contraflow") return null;
  return {
    type: "contraflow",
    linksLayerId: String(raw.linksLayerId || raw.links_layer_id || "").trim(),
    destinationConfigName: String(
      raw.destinationConfigName || raw.destination_config_name || raw.destinationConfig || ""
    ).trim(),
    contraflowEntries: normalizeContraflowEntries(
      raw.value || raw.values || raw.contraflowEntries || raw.contraflow_entries || raw.linkIds || raw.link_ids || []
    ),
  };
}

export function parseDestinationConfigEntry(raw) {
  if (Array.isArray(raw)) {
    return {
      type: "destination",
      nodeLayerId: "",
      visibilityLinksLayerId: "",
      destinationIds: normalizeDestinationIds(raw),
    };
  }
  if (!raw || typeof raw !== "object") return null;

  const type = String(raw.type || "destination").toLowerCase();
  if (type !== "destination") return null;
  const nodeLayerId = String(
    raw.nodeLayerId || raw.node_layer_id || raw.nodesLayerId || raw.nodes_layer_id || ""
  ).trim();
  const visibilityLinksLayerId = String(
    raw.visibilityLinksLayerId || raw.visibility_links_layer_id || ""
  ).trim();
  const destinationIds = normalizeDestinationIds(raw.value || raw.values || raw.destinations || []);
  return { type: "destination", nodeLayerId, visibilityLinksLayerId, destinationIds };
}

export function parseHazardLayerConfigEntry(raw) {
  if (!raw || typeof raw !== "object") return null;
  const type = String(Array.isArray(raw) ? "hazard_layer" : raw.type || "hazard_layer").toLowerCase();
  if (type !== "hazard_layer") return null;
  const sourceEntries = Array.isArray(raw)
    ? raw
    : Array.isArray(raw.hazardEntries)
      ? raw.hazardEntries
      : Array.isArray(raw.hazard_entries)
        ? raw.hazard_entries
        : Array.isArray(raw.hazards)
          ? raw.hazards
          : [raw];
  const hazardEntries = sourceEntries.map((entry, index) => {
    const reroutingRaw = entry?.hazardRerouting ?? entry?.hazard_rerouting ?? false;
    return {
      name: String(entry?.name || `hazard_layer_${index + 1}`).trim(),
      hazardLayerId: String(entry?.hazardLayerId || entry?.hazard_layer_id || "").trim(),
      hazardLayerName: String(entry?.hazardLayerName || entry?.hazard_layer_name || "").trim(),
      hazardRerouting: typeof reroutingRaw === "string"
        ? reroutingRaw.trim().toLowerCase() === "true"
        : Boolean(reroutingRaw),
    };
  });
  return {
    type: "hazard_layer",
    hazardEntries,
  };
}

function defaultStartTimesConfig() {
  return {
    type: "start_times",
    minDepartureTime: 0,
    maxDepartureTime: 0,
    totalPopulationPct: 100,
    granularSurvey: false,
    startTimeRanges: [],
  };
}

function parseStartTimesConfigEntry(raw) {
  const parsed = parseDepartureTimeConfig(raw);
  if (!parsed) return null;
  return {
    type: "start_times",
    minDepartureTime: parsed.minDepartureTime,
    maxDepartureTime: parsed.maxDepartureTime,
    totalPopulationPct: 100,
    granularSurvey: parsed.granularSurvey,
    startTimeRanges: parsed.startTimeRanges,
  };
}

function parseFirestoreCustomConfigEntry(raw) {
  if (Array.isArray(raw)) return parseDestinationConfigEntry(raw);
  if (!raw || typeof raw !== "object") return null;
  const type = String(raw.type || "destination").toLowerCase();
  if (type === "destination") return parseDestinationConfigEntry(raw);
  if (type === "contraflow") return parseContraflowConfigEntry(raw);
  if (type === "blocked_road") return parseBlockedRoadConfigEntry(raw);
  if (type === "start_times") {
    const parsed = parseStartTimesConfigEntry(raw) || defaultStartTimesConfig();
    return { ...defaultStartTimesConfig(), ...parsed };
  }
  if (type === "hazard_layer") return parseHazardLayerConfigEntry(raw);
  return null;
}

export function mergeCustomConfigVars(firestoreCustomConfig, storageConfig) {
  const byName = new Map();

  if (firestoreCustomConfig && typeof firestoreCustomConfig === "object") {
    Object.entries(firestoreCustomConfig).forEach(([name, raw]) => {
      const key = String(name || "").trim();
      if (!key) return;
      const parsed = parseFirestoreCustomConfigEntry(raw);
      if (!parsed) return;
      if (parsed.type === "destination") {
        byName.set(key, {
          name: key,
          type: "destination",
          nodeLayerId: parsed.nodeLayerId,
          visibilityLinksLayerId: parsed.visibilityLinksLayerId,
          destinationIds: parsed.destinationIds,
        });
        return;
      }
      if (parsed.type === "contraflow") {
        byName.set(key, { name: key, ...parsed });
        return;
      }
      if (parsed.type === "blocked_road") {
        byName.set(key, { name: key, ...parsed });
        return;
      }
      if (parsed.type === "start_times") {
        byName.set(key, {
          name: key,
          ...defaultStartTimesConfig(),
          ...parsed,
        });
      }
      if (parsed.type === "hazard_layer") {
        byName.set(key, { name: key, ...parsed });
      }
    });
  }

  if (storageConfig && typeof storageConfig === "object") {
    Object.entries(storageConfig).forEach(([name, raw]) => {
      const key = String(name || "").trim();
      if (!key) return;

      if (Array.isArray(raw)) {
        const existing = byName.get(key);
        if (existing?.type === "contraflow") {
          byName.set(key, { ...existing, contraflowEntries: normalizeContraflowEntries(raw) });
          return;
        }
        if (existing?.type === "blocked_road") {
          byName.set(key, { ...existing, blockedRoadEntries: normalizeBlockedRoadEntries(raw) });
          return;
        }
        if (existing?.type === "hazard_layer") {
          const parsedHazard = parseHazardLayerConfigEntry(raw);
          byName.set(key, { ...existing, ...parsedHazard });
          return;
        }
        const shouldInclude = byName.has(key) || key === "destinations";
        if (!shouldInclude) return;
        const ids = normalizeDestinationIds(raw);
        if (existing && existing.type !== "destination") return;
        if (existing) {
          byName.set(key, { ...existing, destinationIds: ids });
        } else {
          byName.set(key, {
            name: key,
            type: "destination",
            nodeLayerId: "",
            visibilityLinksLayerId: "",
            destinationIds: ids,
          });
        }
        return;
      }

      const parsedStartTimes = parseStartTimesConfigEntry(raw);
      const parsedHazard = parseHazardLayerConfigEntry(raw);
      const existing = byName.get(key);
      if (existing?.type === "hazard_layer" && parsedHazard) {
        byName.set(key, { name: key, ...(existing || {}), ...parsedHazard });
        return;
      }
      if (!parsedStartTimes) return;
      const shouldInclude = byName.has(key) || key === "departure_times";
      if (!shouldInclude) return;
      if (existing && existing.type !== "start_times") return;
      if (existing) {
        byName.set(key, {
          ...existing,
          minDepartureTime: parsedStartTimes.minDepartureTime,
          maxDepartureTime: parsedStartTimes.maxDepartureTime,
          totalPopulationPct: 100,
          granularSurvey: parsedStartTimes.granularSurvey,
          startTimeRanges: parsedStartTimes.startTimeRanges,
        });
      } else {
        byName.set(key, {
          name: key,
          ...defaultStartTimesConfig(),
          ...parsedStartTimes,
        });
      }
    });
  }

  if (!byName.has("destinations")) {
    byName.set("destinations", {
      name: "destinations",
      type: "destination",
      nodeLayerId: "",
      visibilityLinksLayerId: "",
      destinationIds: [],
    });
  }

  if (!byName.has("departure_times")) {
    byName.set("departure_times", {
      name: "departure_times",
      ...defaultStartTimesConfig(),
    });
  }

  return sortCustomConfigVars(Array.from(byName.values()));
}

export function idsEqual(a, b) {
  const left = String(a == null ? "" : a).trim();
  const right = String(b == null ? "" : b).trim();
  if (left === right) return true;

  const nLeft = Number(left);
  const nRight = Number(right);
  if (Number.isFinite(nLeft) && Number.isFinite(nRight)) return nLeft === nRight;

  return false;
}
